import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLandAssets } from "@/hooks/useLandAssets";
import { useCreateLegalFeeStages } from "@/hooks/useLegalFeeStages";
import { STAGE_TEMPLATE } from "@/pages/fixedAssets/components/fixed-asset-detail/legal-fees/legalFees.utils";
import {
  fixedAssetFormSchema,
  FixedAssetFormData,
  ASSET_TYPES,
  LOCATION_ASSET_TYPES,
} from "./fixedAssetForm.schema";
import type { FixedAsset } from "@/hooks/useFixedAssets";
import { useFixedAssetDetail } from "@/hooks/useFixedAssetDetail";

const CURRENCIES = [
  "USD", "EUR", "GBP", "KES", "NGN", "ZAR", "INR", "JPY", "CAD", "AUD",
  "HKD", "SGD",
];

// Editable stage shape used in the inline LegalFeeStageConfig
interface EditableStageItem {
  title: string;
}

interface EditableStage {
  name: string;
  items: EditableStageItem[];
}

function buildEditableStages(): EditableStage[] {
  return STAGE_TEMPLATE.map((s) => ({
    name: s.name,
    items: s.items.map((title) => ({ title })),
  }));
}

interface FixedAssetFormProps {
  asset?: FixedAsset;
  onSave: (data: FixedAssetFormData, file?: File | null) => Promise<{ id: string } | undefined | void>;
  onCancel: () => void;
}

export function FixedAssetForm({ asset, onSave, onCancel }: FixedAssetFormProps) {
  const { user } = useAuth();
  const { data: landAssets = [] } = useLandAssets();
  const createLegalFeeStages = useCreateLegalFeeStages();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ownershipMode, setOwnershipMode] = useState<"url" | "file">("url");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [legalFeeStages, setLegalFeeStages] = useState<EditableStage[]>(buildEditableStages());

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FixedAssetFormData>({
    resolver: zodResolver(fixedAssetFormSchema),
    defaultValues: {
      name: asset?.name ?? "",
      asset_type: (asset?.asset_type as FixedAssetFormData["asset_type"]) ?? "land",
      currency: asset?.currency ?? "USD",
      value: asset?.value ?? 0,
      purchase_price: asset?.purchase_price ?? 0,
      purchase_date: asset?.purchase_date ?? "",
      location: asset?.location ?? "",
      status: asset?.status ?? "owned",
      notes: asset?.notes ?? "",
      ownership_document_url: asset?.ownership_document_url ?? "",
      tenure_type: (asset?.tenure_type as 'freehold' | 'leasehold' | null) ?? null,
      ownership_status: (asset?.ownership_status as 'fully_owned' | 'purchasing') ?? 'fully_owned',
      amount_paid: asset?.amount_paid ?? 0,
      apartment_number: "",
      expected_rent: 0,
      service_fee: 0,
      management_fee: 0,
      management_fee_vat: 0,
      rent_day_of_month: 1,
      is_occupied: false,
      acres: 0,
      land_id: null,
      track_legal_fees: false,
    },
  });

  const { data: detail } = useFixedAssetDetail(asset?.id);

  useEffect(() => {
    if (detail) {
      reset({
        name: detail.asset.name ?? "",
        asset_type: (detail.asset.asset_type as FixedAssetFormData["asset_type"]) ?? "land",
        currency: detail.asset.currency ?? "USD",
        value: detail.asset.value ?? 0,
        purchase_price: detail.asset.purchase_price ?? 0,
        purchase_date: detail.asset.purchase_date ?? "",
        location: detail.asset.location ?? "",
        status: detail.asset.status ?? "owned",
        notes: detail.asset.notes ?? "",
        ownership_document_url: detail.asset.ownership_document_url ?? "",
        tenure_type: (detail.asset.tenure_type as 'freehold' | 'leasehold' | null) ?? null,
        ownership_status: (detail.asset.ownership_status as 'fully_owned' | 'purchasing') ?? 'fully_owned',
        amount_paid: detail.asset.amount_paid ?? 0,
        apartment_number: detail.apartment?.apartment_number ?? "",
        expected_rent: detail.apartment?.expected_rent ?? 0,
        service_fee: detail.apartment?.service_fee ?? 0,
        management_fee: detail.apartment?.management_fee ?? 0,
        management_fee_vat: detail.apartment?.management_fee_vat ?? 0,
        rent_day_of_month: detail.apartment?.rent_day_of_month ?? 1,
        is_occupied: detail.apartment?.is_occupied ?? false,
        acres: detail.land?.acres ?? 0,
        land_id: detail.house?.land_id ?? null,
      });
    }
  }, [detail, reset]);

  const assetType = watch("asset_type");
  const isOccupied = watch("is_occupied");
  const ownershipStatus = watch("ownership_status");
  const trackLegalFees = watch("track_legal_fees");
  const showLocation = LOCATION_ASSET_TYPES.includes(assetType);
  const isPropertyType = LOCATION_ASSET_TYPES.includes(assetType);

  const onSubmit = async (data: FixedAssetFormData) => {
    const savedAsset = await onSave(data, ownershipMode === "file" ? selectedFile : null);

    // Req 1.4, 1.6: call create_legal_fee_stages RPC only when opt-in is on
    if (data.track_legal_fees && data.ownership_status === "purchasing" && savedAsset?.id) {
      const stages = legalFeeStages
        .filter((s) => s.name.trim().length > 0)
        .map((s, si) => ({
          name: s.name,
          sort_order: si,
          items: s.items
            .filter((it) => it.title.trim().length > 0)
            .map((it, ii) => ({
              title: it.title,
              currency: data.currency,
              amount: 0,
              description: null,
              sort_order: ii,
            })),
        }));
      try {
        await createLegalFeeStages.mutateAsync({ fixedAssetId: savedAsset.id, stages });
      } catch (err: any) {
        toast.error(`Failed to create legal fee stages: ${err?.message ?? "unknown error"}`);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Asset Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Asset Name</Label>
        <Input
          id="name"
          {...register("name")}
          placeholder="e.g. Downtown Apartment"
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      {/* Asset Type + Currency */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="asset_type">Asset Type</Label>
          <Select
            value={assetType}
            onValueChange={(v) => {
              setValue("asset_type", v as FixedAssetFormData["asset_type"]);
              // Reset type-specific fields on type change
              setValue("apartment_number", "");
              setValue("expected_rent", 0);
              setValue("service_fee", 0);
              setValue("management_fee", 0);
              setValue("management_fee_vat", 0);
              setValue("rent_day_of_month", 1);
              setValue("is_occupied", false);
              setValue("acres", 0);
              setValue("land_id", null);
            }}
          >
            <SelectTrigger id="asset_type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={watch("currency")}
            onValueChange={(v) => setValue("currency", v)}
          >
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Value + Purchase Price */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="value">Current Value</Label>
          <Input
            id="value"
            type="number"
            step="0.01"
            {...register("value", { valueAsNumber: true })}
          />
          {errors.value && (
            <p className="text-sm text-destructive">{errors.value.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="purchase_price">Purchase Price</Label>
          <Input
            id="purchase_price"
            type="number"
            step="0.01"
            {...register("purchase_price", { valueAsNumber: true })}
          />
          {errors.purchase_price && (
            <p className="text-sm text-destructive">{errors.purchase_price.message}</p>
          )}
        </div>
      </div>

      {/* Purchase Date + Location (conditional) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="purchase_date">Purchase Date</Label>
          <Input
            id="purchase_date"
            type="date"
            {...register("purchase_date")}
          />
        </div>
        {showLocation && (
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              {...register("location")}
              placeholder="e.g. Nairobi, Kenya"
            />
          </div>
        )}
      </div>

      {/* Property-specific: Tenure Type and Ownership Status */}
      {isPropertyType && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenure_type">Tenure Type</Label>
              <Select
                value={watch("tenure_type") ?? "unknown"}
                onValueChange={(v) =>
                  setValue("tenure_type", v === "unknown" ? null : (v as 'freehold' | 'leasehold'))
                }
              >
                <SelectTrigger id="tenure_type">
                  <SelectValue placeholder="Select tenure type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">Not sure yet</SelectItem>
                  <SelectItem value="freehold">Freehold</SelectItem>
                  <SelectItem value="leasehold">Leasehold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownership_status">Ownership Status</Label>
              <Select
                value={ownershipStatus ?? "fully_owned"}
                onValueChange={(v) =>
                  setValue("ownership_status", v as 'fully_owned' | 'purchasing')
                }
              >
                <SelectTrigger id="ownership_status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fully_owned">Fully Owned</SelectItem>
                  <SelectItem value="purchasing">Still Purchasing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {ownershipStatus === "purchasing" && (
            <div className="space-y-2">
              <Label htmlFor="amount_paid">Amount Paid So Far</Label>
              <Input
                id="amount_paid"
                type="number"
                step="0.01"
                {...register("amount_paid", { valueAsNumber: true })}
                placeholder="e.g. 200000"
              />
              {errors.amount_paid && (
                <p className="text-sm text-destructive">{errors.amount_paid.message}</p>
              )}
            </div>
          )}

          {/* Req 1.1: opt-in toggle — only when ownership_status === 'purchasing' */}
          {ownershipStatus === "purchasing" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="track_legal_fees"
                  checked={trackLegalFees ?? false}
                  onCheckedChange={(v) => {
                    setValue("track_legal_fees", v);
                    // Req 1.6: reset stages to template when toggled back on
                    if (v) setLegalFeeStages(buildEditableStages());
                  }}
                />
                <Label htmlFor="track_legal_fees">Track legal &amp; administrative fees</Label>
              </div>

              {/* Req 1.3: stage config area shown when switch is on */}
              {trackLegalFees && (
                <div className="space-y-3 border rounded-md p-4 bg-muted/30">
                  <p className="text-sm font-medium">Legal Fee Stages</p>
                  {legalFeeStages.map((stage, si) => (
                    <div key={si} className="space-y-2 border rounded p-3 bg-background">
                      <div className="flex items-center gap-2">
                        <Input
                          value={stage.name}
                          placeholder="Stage name"
                          onChange={(e) => {
                            const next = [...legalFeeStages];
                            next[si] = { ...next[si], name: e.target.value };
                            setLegalFeeStages(next);
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove stage"
                          onClick={() => setLegalFeeStages(legalFeeStages.filter((_, i) => i !== si))}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="space-y-1 pl-2">
                        {stage.items.map((item, ii) => (
                          <div key={ii} className="flex items-center gap-2">
                            <Input
                              value={item.title}
                              placeholder="Item title"
                              onChange={(e) => {
                                const next = [...legalFeeStages];
                                const items = [...next[si].items];
                                items[ii] = { title: e.target.value };
                                next[si] = { ...next[si], items };
                                setLegalFeeStages(next);
                              }}
                              className="flex-1 h-8 text-sm"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Remove item"
                              onClick={() => {
                                const next = [...legalFeeStages];
                                next[si] = {
                                  ...next[si],
                                  items: next[si].items.filter((_, i) => i !== ii),
                                };
                                setLegalFeeStages(next);
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs mt-1"
                          onClick={() => {
                            const next = [...legalFeeStages];
                            next[si] = {
                              ...next[si],
                              items: [...next[si].items, { title: "" }],
                            };
                            setLegalFeeStages(next);
                          }}
                        >
                          <PlusCircle className="h-3 w-3 mr-1" />
                          Add item
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLegalFeeStages([...legalFeeStages, { name: "", items: [] }])
                    }
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Add stage
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Apartment-specific fields */}
      {assetType === "apartment" && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="apartment_number">Apartment Number</Label>
              <Input
                id="apartment_number"
                {...register("apartment_number")}
                placeholder="e.g. 4B"
              />
              {errors.apartment_number && (
                <p className="text-sm text-destructive">{errors.apartment_number.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rent_day_of_month">Rent Day of Month</Label>
              <Input
                id="rent_day_of_month"
                type="number"
                min={1}
                max={28}
                {...register("rent_day_of_month", { valueAsNumber: true })}
              />
              {errors.rent_day_of_month && (
                <p className="text-sm text-destructive">{errors.rent_day_of_month.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expected_rent">Expected Rent</Label>
              <Input
                id="expected_rent"
                type="number"
                step="0.01"
                {...register("expected_rent", { valueAsNumber: true })}
              />
              {errors.expected_rent && (
                <p className="text-sm text-destructive">{errors.expected_rent.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="service_fee">Service Fee</Label>
              <Input
                id="service_fee"
                type="number"
                step="0.01"
                {...register("service_fee", { valueAsNumber: true })}
              />
              {errors.service_fee && (
                <p className="text-sm text-destructive">{errors.service_fee.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="management_fee">Management Fee</Label>
              <Input
                id="management_fee"
                type="number"
                step="0.01"
                {...register("management_fee", { valueAsNumber: true })}
              />
              {errors.management_fee && (
                <p className="text-sm text-destructive">{errors.management_fee.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="management_fee_vat">Management Fee VAT</Label>
              <Input
                id="management_fee_vat"
                type="number"
                step="0.01"
                {...register("management_fee_vat", { valueAsNumber: true })}
              />
              {errors.management_fee_vat && (
                <p className="text-sm text-destructive">{errors.management_fee_vat.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="is_occupied"
              checked={isOccupied ?? false}
              onCheckedChange={(v) => setValue("is_occupied", v)}
            />
            <Label htmlFor="is_occupied">Currently Occupied</Label>
          </div>
        </>
      )}

      {/* Land-specific fields */}
      {assetType === "land" && (
        <div className="space-y-2">
          <Label htmlFor="acres">Acres</Label>
          <Input
            id="acres"
            type="number"
            step="0.01"
            {...register("acres", { valueAsNumber: true })}
            placeholder="e.g. 2.5"
          />
          {errors.acres && (
            <p className="text-sm text-destructive">{errors.acres.message}</p>
          )}
        </div>
      )}

      {/* House-specific fields */}
      {assetType === "house" && (
        <div className="space-y-2">
          <Label htmlFor="land_id">Linked Land (optional)</Label>
          <Select
            value={watch("land_id") ?? "none"}
            onValueChange={(v) => setValue("land_id", v === "none" ? null : v)}
          >
            <SelectTrigger id="land_id">
              <SelectValue placeholder="Select land asset (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No linked land</SelectItem>
              {landAssets.map((land) => (
                <SelectItem key={land.id} value={land.id}>
                  {land.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Status (edit mode only) */}
      {asset && (
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={watch("status")}
            onValueChange={(v) => setValue("status", v)}
          >
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owned">Owned</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Input
          id="notes"
          {...register("notes")}
          placeholder="Optional notes"
        />
      </div>

      {/* Ownership Document */}
      <div className="space-y-2">
        <Label>Ownership Document (optional)</Label>
        <div className="flex gap-2 mb-2">
          <Button
            type="button"
            variant={ownershipMode === "url" ? "default" : "outline"}
            size="sm"
            onClick={() => setOwnershipMode("url")}
          >
            URL
          </Button>
          <Button
            type="button"
            variant={ownershipMode === "file" ? "default" : "outline"}
            size="sm"
            onClick={() => setOwnershipMode("file")}
          >
            File Upload
          </Button>
        </div>
        {ownershipMode === "url" ? (
          <Input
            id="ownership_document_url"
            {...register("ownership_document_url")}
            placeholder="https://example.com/document.pdf"
          />
        ) : (
          <div className="space-y-1">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
            {selectedFile && (
              <p className="text-xs text-muted-foreground">
                Selected: {selectedFile.name}
              </p>
            )}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {asset ? "Update" : "Add Asset"}
        </Button>
      </DialogFooter>
    </form>
  );
}
